import React from "react";
import { Card, CardBody, Col, Row, Progress } from "reactstrap";
import { formatCurrency } from "../../utils/formatCurrency";

/**
 * Resumen de una sesión de caja.
 *
 * Muestra por separado tres cosas que antes venían mezcladas en un solo número:
 *   - COBRADO: lo que se vendió, con su desglose por forma de pago.
 *   - FONDO DE CAJA: la plata que ya estaba en el cajón al abrir. No es una
 *     venta, por eso no suma a lo cobrado.
 *   - RETIROS: egresos y devoluciones de la sesión.
 *
 * Y calcula el número que hace falta para el arqueo y que no existía en ningún
 * lado: cuánto EFECTIVO debería haber en el cajón.
 *
 * Ojo con la aritmética, que es donde se equivoca todo el mundo: el efectivo
 * del cajón NO es "cobrado + fondo". La tarjeta y MercadoPago no están en el
 * cajón. Es: fondo + cobrado en efectivo − retirado en efectivo.
 */

const ICONS = {
  Efectivo: "payments",
  MercadoPago: "fab fa-cc-stripe",
  "Mercado Pago": "fab fa-cc-stripe",
  Tarjeta: "credit_card",
  "Tarjeta de Débito": "credit_card",
  "Tarjeta de Crédito": "credit_card",
  Transferencia: "swap_horiz",
};

const COLORS = {
  Efectivo: "success",
  MercadoPago: "info",
  "Mercado Pago": "info",
  Tarjeta: "warning",
  "Tarjeta de Débito": "warning",
  "Tarjeta de Crédito": "warning",
  Transferencia: "primary",
};

const Stat = ({ label, value, hint, color = "secondary", strong = false }) => (
  <div className="d-flex justify-content-between align-items-baseline py-1">
    <div>
      <span
        className={strong ? "fw-bold" : "text-muted"}
        style={{ fontSize: strong ? 13 : 12.5 }}
      >
        {label}
      </span>
      {hint && (
        <div className="text-muted" style={{ fontSize: 10.5, lineHeight: 1.3 }}>
          {hint}
        </div>
      )}
    </div>
    <span
      className={`text-${color}`}
      style={{
        fontSize: strong ? 17 : 14,
        fontWeight: strong ? 700 : 600,
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </span>
  </div>
);

const CashSessionSummary = ({ totals, byPayment }) => {
  if (!totals) return null;

  // Ordenado de mayor a menor: el medio de pago que más pesa queda arriba y se
  // lee de un vistazo, sin tener que comparar porcentajes.
  const payments = Array.isArray(byPayment)
    ? byPayment.filter((p) => p.name !== "Total").sort((a, b) => b.value - a.value)
    : [];
  const cobrado = totals.cobrado || 0;

  return (
    <Row className="g-2 g-md-3 mb-3">
      {/* ── Cobrado + desglose por forma de pago ── */}
      <Col lg={7}>
        <Card className="mb-0 shadow-sm h-100" style={{ borderLeft: "3px solid var(--bs-primary)" }}>
          <CardBody className="py-2 px-3">
            <div className="d-flex justify-content-between align-items-baseline">
              <span className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>
                Cobrado en la sesión
              </span>
              <span
                className="fw-bold"
                style={{ fontSize: 20, fontVariantNumeric: "tabular-nums" }}
              >
                {formatCurrency(cobrado)}
              </span>
            </div>

            {/* Solo si hubo anulaciones o devoluciones: mostrar de dónde sale
                el neto, para que nadie sospeche que falta plata. */}
            {(totals.retiros || 0) > 0 && (
              <div
                className="text-muted text-end mb-1"
                style={{ fontSize: 11, fontVariantNumeric: "tabular-nums" }}
              >
                {formatCurrency(totals.cobradoBruto || 0)} cobrados
                {" − "}
                {formatCurrency(totals.retiros)} en anulaciones y devoluciones
              </div>
            )}
            <div className="mb-2" />

            {payments.length === 0 && (
              <div className="text-muted" style={{ fontSize: 12 }}>
                Sin cobros en esta sesión.
              </div>
            )}

            {payments.map((p, i) => {
              const color = COLORS[p.name] || "secondary";
              const icon = ICONS[p.name] || "receipt";
              const isFa = icon.startsWith("fa");
              const pct = cobrado > 0 ? Math.round((p.value / cobrado) * 100) : 0;
              return (
                <div key={i} className="mb-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: 12.5 }}>
                      {isFa ? (
                        <i className={`${icon} text-${color} me-2`} style={{ fontSize: 14 }}></i>
                      ) : (
                        <span
                          className={`material-symbols-rounded text-${color} me-2`}
                          style={{ fontSize: 16, verticalAlign: "-3px" }}
                        >
                          {icon}
                        </span>
                      )}
                      {p.name}
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                      {formatCurrency(p.value)}
                      <span className="text-muted ms-2" style={{ fontSize: 10.5 }}>
                        {pct}%
                      </span>
                    </span>
                  </div>
                  <Progress value={pct} color={color} style={{ height: 3 }} className="mt-1" />
                </div>
              );
            })}
          </CardBody>
        </Card>
      </Col>

      {/* ── Fondo, retiros y arqueo de efectivo ── */}
      <Col lg={5}>
        <Card className="mb-0 shadow-sm h-100" style={{ borderLeft: "3px solid var(--bs-success)" }}>
          <CardBody className="py-2 px-3">
            <Stat
              label="Fondo de caja"
              hint="Ya estaba al abrir — no es una venta"
              value={formatCurrency(totals.fondo || 0)}
            />
            <Stat
              label="Retiros y devoluciones"
              value={
                (totals.retiros || 0) > 0
                  ? `− ${formatCurrency(totals.retiros)}`
                  : formatCurrency(0)
              }
              color={(totals.retiros || 0) > 0 ? "danger" : "secondary"}
            />

            <hr className="my-2" />

            {totals.tieneEfectivo ? (
              <Stat
                label="Efectivo en caja"
                hint="fondo + cobrado en efectivo − retiros en efectivo"
                value={formatCurrency(totals.efectivoEnCaja || 0)}
                color="success"
                strong
              />
            ) : (
              <div className="text-muted" style={{ fontSize: 11.5 }}>
                No hubo movimientos en efectivo en esta sesión.
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default CashSessionSummary;
